/**
 * Cookie Consent Component for GDPR/CCPA Compliance
 *
 * Provides cookie consent management with configurable storage key.
 * Uses @puffin-core/storage for persistence with memory-first pattern.
 *
 * ADMIN-PG-CLOSURE-001 / Phase 6 (2026-05-21): the `t` prop was removed
 * from both `CookieConsentBanner` and `CookieConsentProvider`. The banner
 * reads its words from context — since LIFE-SHORTLISTED-001 F-Q7-1, through
 * `useMarketingShellTranslate()`, marketing-shell's own namespace. This removes the
 * static-prop coupling that blocked the canonical `<CustomerPortalShell>`
 * (PS-005) migration: shells compose providers via component+props
 * factories where the props are built BEFORE any hook can run, so a
 * hook-derived `t` value cannot be passed through them. With this
 * refactor, the banner pulls `t` from context internally.
 *
 * @module @puffin-core/marketing-shell/CookieConsent
 */

import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useMarketingShellTranslate } from '../hooks/useMarketingShellTranslate.js';
import { setTelemetryConsent, TELEMETRY_CONSENT } from '@puffin-core/logging-browser';
import { readGlobalPrivacyControl } from '../consent/privacySignal.js';

const CookieConsentContext = createContext(null);

/**
 * Hook to access cookie consent state and actions.
 *
 * @returns {{ hasConsent: boolean, showBanner: boolean, acceptCookies: Function, declineCookies: Function,
 *   reopenBanner: Function, canReopen: boolean, privacySignal: boolean, privacyHref: ?string }}
 * @throws {Error} If used outside CookieConsentProvider
 */
export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};

/**
 * The consent context where there may be none: `null` outside every provider. For a control that must render
 * nothing on a tree with no consent surface (`<CookieSettingsButton>` on the server-rendered blog).
 *
 * @returns {?Object} the context `useCookieConsent()` answers, or null
 */
export const useOptionalCookieConsent = () => useContext(CookieConsentContext);

/**
 * Cookie consent banner component.
 *
 * Its words are marketing-shell's own (`marketingShell` namespace: cookieConsent.title, .message,
 * .privacyLink, .moreInfo, .decline, .accept) — ONE wording on every Colony marketing site and on the
 * tenant site (LIFE-SHORTLISTED-001 F-Q7-1). The sites used to carry five different copies.
 */
const CookieConsentBanner = () => {
  const t = useMarketingShellTranslate();
  const { showBanner, acceptCookies, declineCookies, privacyHref, privacySignal } =
    useCookieConsent();

  if (!showBanner) {
    return null;
  }

  return (
    <div
      className="consent-banner"
      role="dialog"
      aria-labelledby="cookie-title"
      aria-describedby="cookie-desc"
    >
      <div className="consent-banner-content">
        <h2 id="cookie-title" className="consent-banner-title">
          {t('cookieConsent.title')}
        </h2>
        <p id="cookie-desc" className="consent-banner-message">
          {t('cookieConsent.message')}{' '}
          {/* 🔴 Was a hardcoded `/privacy` (NUDGE-LINK-001 R-31 / Q-P3F-3, 2026-09-10). Four
              surfaces declare no such route at all, and every app that DOES declare one mounts it
              inside the AUTHENTICATED tree — while this banner is shown to an unauthenticated
              visitor, so the relative link bounced exactly its own audience to `/login`. The host
              supplies an absolute public URL; see `marketingPrivacyUrl`. */}
          <a href={privacyHref} className="consent-banner-link">
            {t('cookieConsent.privacyLink')}
          </a>{' '}
          {t('cookieConsent.moreInfo')}
        </p>
        {privacySignal ? (
          <p className="consent-banner-message">{t('cookieConsent.gpcNotice')}</p>
        ) : null}
        <div className="consent-banner-actions">
          <button type="button" className="btn btn-secondary" onClick={declineCookies}>
            {t('cookieConsent.decline')}
          </button>
          <button type="button" className="btn btn-primary" onClick={acceptCookies}>
            {t('cookieConsent.accept')}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * How long a visitor's choice stands before the banner asks again: 12 months (LIFE-SHORTLISTED-001 P4). Regulator
 * guidance caps it (the CNIL recommends at most 13 months for a cookie choice); 12 months is the common practice under
 * that cap. A choice with no readable timestamp counts as expired, so the visitor is asked rather than assumed.
 */
export const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

/**
 * Whether a stored choice still stands.
 *
 * @param {*} storedConsent - the stored `{accepted, timestamp}`
 * @param {number} now - epoch milliseconds
 * @returns {boolean}
 */
function isCurrentChoice(storedConsent, now) {
  if (!storedConsent || typeof storedConsent.accepted !== 'boolean') return false;
  const madeAt = Date.parse(storedConsent.timestamp);
  return Number.isFinite(madeAt) && madeAt <= now && now - madeAt < CONSENT_MAX_AGE_MS;
}

/**
 * Read initial consent state from storage. A choice older than {@link CONSENT_MAX_AGE_MS}, or one that cannot be read,
 * is asked again.
 *
 * Global Privacy Control: a visitor with NO current choice whose browser sends the signal has declined — no
 * banner asks, and nothing is stored, so the signal is read again on every visit. A stored explicit choice wins.
 *
 * @param {string} storageKey - Storage key for consent preference
 * @param {Object} storageAvailableRef - Ref tracking storage availability
 * @param {Object} storage - the site's ONE `@puffin-core/storage` StorageService instance (never the class)
 * @param {string} STORAGE_TYPE_LOCAL - LOCAL constant from @puffin-core/storage
 * @param {number} [now=Date.now()] - epoch milliseconds (injectable for tests)
 * @param {boolean} [privacySignal=readGlobalPrivacyControl()] - whether the browser sends Global Privacy Control
 * @returns {Object} Initial consent state
 */
export function getInitialConsentState(
  storageKey,
  storageAvailableRef,
  storage,
  STORAGE_TYPE_LOCAL,
  now = Date.now(),
  privacySignal = readGlobalPrivacyControl(),
) {
  if (typeof window === 'undefined') {
    return { hasConsent: false, showBanner: true, isLoaded: false };
  }

  try {
    storageAvailableRef.current = storage.isStorageAvailable(STORAGE_TYPE_LOCAL);

    if (storageAvailableRef.current) {
      const storedConsent = storage.getJSON(storageKey, STORAGE_TYPE_LOCAL);
      if (isCurrentChoice(storedConsent, now)) {
        return {
          hasConsent: storedConsent.accepted,
          showBanner: false,
          isLoaded: true,
        };
      }
    }

    return { hasConsent: false, showBanner: !privacySignal, isLoaded: true };
  } catch {
    storageAvailableRef.current = false;
    return { hasConsent: false, showBanner: !privacySignal, isLoaded: true };
  }
}

/**
 * The telemetry decision a consent state stands for: an unanswered banner is PENDING (F-SG-2); a
 * stored answer is GRANTED only when it is literally `true`, and DECLINED otherwise.
 *
 * @param {{hasConsent: *, showBanner: boolean}} state - from {@link getInitialConsentState}
 * @returns {string} one of `TELEMETRY_CONSENT`
 */
export function telemetryDecisionFor({ hasConsent, showBanner }) {
  if (showBanner) return TELEMETRY_CONSENT.PENDING;
  return hasConsent === true ? TELEMETRY_CONSENT.GRANTED : TELEMETRY_CONSENT.DECLINED;
}

/**
 * Cookie consent provider component.
 *
 * Memory-first pattern:
 * - Reads from storage ONCE on initialization
 * - Memory state is authoritative after initialization
 * - Storage writes are opportunistic (fire-and-forget)
 *
 * ADMIN-PG-CLOSURE-001 / Phase 6: the `t` prop was removed. The banner
 * (mounted by this provider) reads marketing-shell's own namespace through
 * `useMarketingShellTranslate()` (F-Q7-1).
 *
 * @param {Object} props
 * @param {string} props.storageKey - Storage key for consent preference (e.g., 'plannerpuffinCookieConsent')
 * @param {Object} props.storage - the site's ONE `@puffin-core/storage` StorageService instance (never the class;
 *   N198 batch 6: no process-wide singleton)
 * @param {string} props.STORAGE_TYPE_LOCAL - LOCAL constant from STORAGE_TYPE
 * @param {Function} [props.onConsentChange] - Optional callback when consent changes: (accepted: boolean) => void
 * @param {React.ReactNode} props.children - Child components
 */
export const CookieConsentProvider = ({
  storageKey,
  storage,
  STORAGE_TYPE_LOCAL,
  onConsentChange,
  privacyHref,
  children,
}) => {
  const storageAvailableRef = useRef(true);

  // BLOG-STD-001 full-shell SSR: the first render must be DETERMINISTIC
  // across server (Node prerender, no browser globals) and client (hydration,
  // browser globals available). Reading localStorage inside the useState
  // initializer breaks determinism — the server sees no storage and returns
  // isLoaded:false (banner is null because of `{isLoaded && <Banner />}`),
  // the client reads storage synchronously and returns isLoaded:true (banner
  // mounted) → React hydration mismatch (#418). The canonical React 18 SSR
  // pattern: initialize to the deterministic "loading" state matching the
  // server, then read storage in a post-mount useEffect. The banner "pops in"
  // after the first paint instead of mounting during hydration. Pre-fix this
  // mismatch was masked by the CSP-blocked SSR seed (full client re-render
  // hid the diff); the BLOG-STD-001 CSP fix exposed it.
  const [consentState, setConsentState] = useState({
    hasConsent: false,
    showBanner: false,
    isLoaded: false,
  });

  // Read storage AFTER the initial commit so server + client agree on the
  // first paint, then update state on the next tick.
  //
  // UNREACHABLE-MODULE-BURNDOWN-001 Q-UMB-13: this provider is also the page's TELEMETRY consent
  // surface. Reporting the stored decision here marks every page that renders a consent banner as
  // consent-governed, so the browser's web-vitals collector (`@puffin-core/logging-browser`) sends
  // nothing until the visitor accepts. An unanswered banner is PENDING, never a decline (F-SG-2):
  // the collector keeps the vitals measured before this mount, so a first-visit Accept releases them.
  // Errors are not governed — see `telemetryConsent.js` for the split.
  useEffect(() => {
    const initial = getInitialConsentState(
      storageKey,
      storageAvailableRef,
      storage,
      STORAGE_TYPE_LOCAL,
    );
    setConsentState(initial);
    setTelemetryConsent(telemetryDecisionFor(initial));
  }, [storageKey, storage, STORAGE_TYPE_LOCAL]);

  const persistConsent = useCallback(
    (accepted) => {
      if (!storageAvailableRef.current) return;

      try {
        storage.setJSON(
          storageKey,
          {
            accepted,
            timestamp: new Date().toISOString(),
          },
          STORAGE_TYPE_LOCAL,
        );
      } catch {
        storageAvailableRef.current = false;
      }
    },
    [storage, storageKey, STORAGE_TYPE_LOCAL],
  );

  const acceptCookies = useCallback(() => {
    setConsentState({ hasConsent: true, showBanner: false, isLoaded: true });
    persistConsent(true);
    setTelemetryConsent(TELEMETRY_CONSENT.GRANTED);
    onConsentChange?.(true);
  }, [persistConsent, onConsentChange]);

  const declineCookies = useCallback(() => {
    setConsentState({ hasConsent: false, showBanner: false, isLoaded: true });
    persistConsent(false);
    setTelemetryConsent(TELEMETRY_CONSENT.DECLINED);
    onConsentChange?.(false);
  }, [persistConsent, onConsentChange]);

  // "Cookie settings": the banner asks again. The choice in force (and the telemetry decision made from it)
  // stands until the visitor answers, so reopening unlocks and locks nothing by itself.
  const reopenBanner = useCallback(() => {
    setConsentState((current) => ({ ...current, showBanner: true }));
  }, []);

  const { hasConsent, showBanner, isLoaded } = consentState;

  const contextValue = {
    hasConsent,
    showBanner,
    acceptCookies,
    declineCookies,
    reopenBanner,
    // Nothing to reopen until the stored choice has been read: the server and the first client render agree.
    canReopen: isLoaded,
    // Read once the stored choice has been (never during the server-matching first render).
    privacySignal: isLoaded && readGlobalPrivacyControl(),
    privacyHref,
  };

  return (
    <CookieConsentContext.Provider value={contextValue}>
      {children}
      {isLoaded && <CookieConsentBanner />}
    </CookieConsentContext.Provider>
  );
};

CookieConsentProvider.propTypes = {
  storageKey: PropTypes.string.isRequired,
  storage: PropTypes.shape({
    isStorageAvailable: PropTypes.func.isRequired,
    getJSON: PropTypes.func.isRequired,
    setJSON: PropTypes.func.isRequired,
  }).isRequired,
  STORAGE_TYPE_LOCAL: PropTypes.string.isRequired,
  onConsentChange: PropTypes.func,
  // REQUIRED, and deliberately with NO default. D-4 doctrine: the retired
  // `/admin/settings/dashboard-groups` default was a dead link in six of seven consumers, and a
  // `/privacy` default was a dead link here. A required prop makes a new mount state its
  // destination instead of inheriting a wrong one silently.
  privacyHref: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

// React 19 ignores `defaultProps` on function components, so the block that
// stood here was inert. `onConsentChange: undefined` was a no-op —
// an omitted prop is already `undefined`.

const CONSENT_UNRECORDABLE =
  'Consent cannot be recorded on a surface with no privacy notice (ConsentWithheldProvider)';

function refuseToRecordConsent() {
  throw new Error(CONSENT_UNRECORDABLE);
}

/** The fixed context of a surface that cannot ask for consent. */
const WITHHELD_CONSENT = Object.freeze({
  hasConsent: false,
  showBanner: false,
  acceptCookies: refuseToRecordConsent,
  declineCookies: refuseToRecordConsent,
  reopenBanner: refuseToRecordConsent,
  canReopen: false,
  privacySignal: false,
  privacyHref: null,
});

/**
 * Consent provider for a surface that has NO privacy notice to link (LIFE-SHORTLISTED-001 Q-LS-10,
 * owner ruling "fail closed"): consent is only informed when the visitor can read the notice it
 * refers to, so no banner asks for it and nothing consent would unlock is ever recorded. Supplies
 * the same context `useCookieConsent()` reads — fixed at "withheld" — so analytics providers and
 * page-view effects below keep working and simply never see consent. A choice can never be
 * recorded here (there is no banner to make one); the two actions throw rather than pretend.
 *
 * It is the page's TELEMETRY consent surface too, DECLINED from mount (F-SG-3): a page no surface
 * governs releases its web vitals at the first hide, so without this report a tenant site with no
 * privacy notice shipped vitals with no consent — the opposite of "fail closed".
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {React.JSX.Element}
 */
export const ConsentWithheldProvider = ({ children }) => {
  useEffect(() => {
    setTelemetryConsent(TELEMETRY_CONSENT.DECLINED);
  }, []);
  return (
    <CookieConsentContext.Provider value={WITHHELD_CONSENT}>
      {children}
    </CookieConsentContext.Provider>
  );
};

ConsentWithheldProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
